import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { BeneficiaryType, Routes } from "@ctypes";
import {
  useNextTransfer,
  usePortfolioPerformanceGraph,
  usePromotionalDollarQuery,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { calculateAccountBalance, formatCurrency, getWeeklyRecurringTotal } from "@utils";
import { ArtPanel, Card, FilledButton } from "./styles";
import { home } from "./tokens";

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
`;

const RailLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${home.ink48};
`;

const ValueCard = styled(ArtPanel)`
  flex-grow: 1;
  min-height: 200px;
  background:
    radial-gradient(440px 320px at 92% -6%, rgba(143, 191, 176, 0.42), transparent 66%),
    radial-gradient(380px 340px at 0% 104%, rgba(78, 102, 124, 0.3), transparent 68%),
    linear-gradient(150deg, #17231f 0%, #10141c 100%);
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
`;

const ValueAmount = styled.div`
  font-size: 40px;
  font-weight: 600;
  letter-spacing: -1.2px;
  line-height: 1;
`;

const ReturnChip = styled.div<{ $down?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: ${home.sageChipBg};
  font-size: 12px;
  font-weight: 600;
  color: ${home.sage};

  svg {
    transform: ${({ $down }) => ($down ? "rotate(180deg)" : "none")};
  }
`;

const PromoCard = styled.div`
  flex-shrink: 0;
  border-radius: 18px;
  border: 1px solid ${home.promoBorder};
  background: ${home.promoBg};
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PromoAmount = styled.div`
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.7px;
  line-height: 1;
`;

const TransferCard = styled(Card)`
  flex-shrink: 0;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  border-color: rgba(151, 179, 208, 0.28);
  background:
    radial-gradient(360px 200px at 100% 0%, rgba(151, 179, 208, 0.2), transparent 66%),
    ${home.cardBg};

  &:hover {
    border-color: rgba(151, 179, 208, 0.45);
  }
`;

const TransferIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: rgba(151, 179, 208, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TransferAmount = styled.div`
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.4px;
  line-height: 1;

  span {
    font-size: 13px;
    font-weight: 400;
    color: ${home.ink46};
  }
`;

export const MoneyRail = () => {
  const navigate = useNavigate();
  const { beneficiaries } = useUser();
  const { data: nextTransfer } = useNextTransfer();
  const { promotionalBalance } = usePromotionalDollarQuery();

  const selfBeneficiaryId = beneficiaries?.find((b) => b?.type === BeneficiaryType.Self)?.id;
  const { data: performance } = usePortfolioPerformanceGraph({
    variables: { beneficiaryId: selfBeneficiaryId },
    skip: !selfBeneficiaryId,
  });

  const balance = calculateAccountBalance(beneficiaries);

  const allTimeReturn = performance?.fiveYear?.grossReturnAbsolute ?? 0;

  const weeklyTotal =
    beneficiaries?.reduce((sum, b) => sum + getWeeklyRecurringTotal({ beneficiary: b }), 0) ?? 0;

  const splits = (beneficiaries ?? [])
    .map((b) => ({
      name: b?.type === BeneficiaryType.Self || !b?.firstName ? null : b.firstName,
      weekly: getWeeklyRecurringTotal({ beneficiary: b }),
    }))
    .filter((s) => s.weekly > 0);

  return (
    <Rail>
      <ValueCard>
        <RailLabel>{i18n.t(Translations.HomeCurrentValueLabel)}</RailLabel>
        <ValueAmount>{formatCurrency(balance, true)}</ValueAmount>
        {allTimeReturn !== 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ReturnChip $down={allTimeReturn < 0}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                style={{ stroke: home.sage }}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 15 6-6 6 6" />
              </svg>
              {formatCurrency(Math.abs(allTimeReturn), true)}
            </ReturnChip>
            <span style={{ fontSize: 12, color: home.ink44 }}>
              {i18n.t(Translations.HomeAllTime)}
            </span>
          </div>
        )}
      </ValueCard>

      {promotionalBalance > 0 && (
        <PromoCard>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <RailLabel style={{ color: home.ink62 }}>
              {i18n.t(Translations.HomePromoLabel)}
            </RailLabel>
            <PromoAmount>{formatCurrency(promotionalBalance, true)}</PromoAmount>
          </div>
          <FilledButton
            style={{ marginLeft: "auto", padding: "0 20px" }}
            onClick={() => navigate(Routes.AllocateFunds)}
          >
            {i18n.t(Translations.HomeAllocate)}
          </FilledButton>
        </PromoCard>
      )}

      <TransferCard onClick={() => navigate(Routes.Transfers)}>
        <TransferIcon>
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            style={{ stroke: home.ink72 }}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </TransferIcon>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 12, color: home.ink50 }}>
            {i18n.t(Translations.HomeNextTransfer)}
            {nextTransfer?.date ? ` · ${dayjs(nextTransfer.date).format("MMM D, YYYY")}` : ""}
          </div>
          <TransferAmount>
            {formatCurrency(Math.round(weeklyTotal))} <span>{i18n.t(Translations.HomeWeekly)}</span>
          </TransferAmount>
          {splits.length > 0 && (
            <div style={{ fontSize: 12, color: home.ink38 }}>
              {splits.length <= 2
                ? splits
                    .map((s) =>
                      s.name
                        ? i18n.t(Translations.HomeSplitToName, {
                            amount: formatCurrency(Math.round(s.weekly)),
                            name: s.name,
                          })
                        : i18n.t(Translations.HomeSplitToYou, {
                            amount: formatCurrency(Math.round(s.weekly)),
                          }),
                    )
                    .join(" · ")
                : i18n.t(Translations.HomeSplitAcross, {
                    count: splits.length,
                  })}
            </div>
          )}
        </div>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ stroke: home.ink42, marginLeft: "auto", flexShrink: 0 }}
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
      </TransferCard>
    </Rail>
  );
};
